/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { func, object, string, bool } from "prop-types";
import cogoToast from "cogo-toast";

import CustomInput from "../../../components/CustomInput";
import SearchLocationInput from "../../../components/LocationComponent";
import CustomSelect from "../../../components/CustomSelect";
import AIJobDescriptionIcons from "../../../components/AI/AIJobDescriptionIcons";
import JDMethodSelector from "../../../components/JDMethodSelector";
import JDFileUploader from "../../../components/JDFileUploader";
import { getCountryAction } from "../../../redux/actions/countryActions";
import { aiJobDescriptionService } from "../../../services/aiJobDescriptionService";
import { CURRENCY_OPTIONS } from "../../../utils/constants";
import icons from "../../../assets/icons/icon-collection.svg";
import { Tooltip } from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { Link } from "react-router-dom";
import {
  createMissingSkillsIfAny,
  getAllSkillsAction,
  createCategorySkillAction,
} from "../../../redux/actions/skillsAction";
import CloseIcon from "@material-ui/icons/Close";
import { useTheme, useMediaQuery } from "@material-ui/core";

// Quill Editor Configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["blockquote"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "blockquote",
];
const JobDetails = ({
  handleChange,
  updateSelectedCountryID,
  industryOptions,
  levelOptions,
  skillOptions,
  setNewJob,
  newJob,
  duplicateData,
  isOnDuplicatePost,
}) => {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [externalPlatforms, setExternalPlatforms] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [jdMethod, setJdMethod] = useState(null); // null | "upload" | "ai" | "manual"
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [pendingContent, setPendingContent] = useState(null);
  const [pendingMethod, setPendingMethod] = useState(null);
  const dispatch = useDispatch();
  const [state, setState] = useState({
    country: selectedCountry || 0,
  });

  const { country, auth } = useSelector((store) => store);
  const { countries } = country;
  const { userData } = auth;

  useEffect(() => {
    getCountryAction(dispatch);
  }, [dispatch]);

  // Sync local state when newJob changes (e.g. template pre-fill)
  useEffect(() => {
    if (newJob.skills_required && newJob.skills_required.length > 0) {
      const normalized = newJob.skills_required
        .map((s) => (typeof s === "string" ? s : s?.title || s?.name || ""))
        .filter(Boolean);
      setSelectedSkills(normalized);
    }
    if (newJob.country) {
      setSelectedCountry(newJob.country);
      setState((prev) => ({ ...prev, country: newJob.country }));
    }
  }, [newJob.skills_required, newJob.country]);

  const addSkill = ({ target: { value } }) => {
    const skills = value;
    setSelectedSkills(skills);
    handleChange({ target: { value: skills, name: "skills_required" } });
  };

  const handleSkillsChange = (_event, value) => {
    const normalized = value.map((v) =>
      typeof v === "string" ? v : v?.inputValue ? v.inputValue : v?.title || v,
    );
    setSelectedSkills(normalized);
    handleChange({ target: { value: normalized, name: "skills_required" } });
    // Persist new skills to category-create endpoint with title & description
    const existingTitles = new Set(
      (skillOptions || []).map(({ title }) => (title || "").toLowerCase()),
    );
    const newTitles = normalized
      .map((t) => (t || "").trim())
      .filter((t) => t && !existingTitles.has(t.toLowerCase()));
    if (newTitles.length > 0) {
      Promise.all(
        newTitles.map((title) =>
          createCategorySkillAction({ title, description: title }, dispatch),
        ),
      ).then(() => getAllSkillsAction(dispatch));
    }
  };

  const handleExternalPlatformChange = (platform) => {
    let updatedPlatforms;
    if (externalPlatforms.includes(platform)) {
      updatedPlatforms = externalPlatforms.filter((p) => p !== platform);
    } else {
      updatedPlatforms = [...externalPlatforms, platform];
    }
    setExternalPlatforms(updatedPlatforms);
    handleChange({
      target: { value: updatedPlatforms, name: "external_platforms" },
    });
  };

  const displaySkills = () => {
    return selectedSkills.map((skill, index) => (
      <span
        className="d-flex align-items-center justify-content-between mt-2 mr-2 py-1 pl-3 outlined"
        key={index.toString()}
      >
        {skill}
        <IconButton onClick={() => removeSkill(skill)}>
          <svg width="10" height="8">
            <use href={`${icons}#close-blue`}></use>
          </svg>
        </IconButton>
      </span>
    ));
  };

  const handleChangeCountry = ({ target: { value } }) => {
    const countryID = value;
    setSelectedCountry(countryID);
    setState({ ...newJob, country: countryID });
    updateSelectedCountryID(countryID);
  };

  const removeSkill = (skillToRemove) => {
    const skillArray = [...selectedSkills];
    const newSkillArray = skillArray.filter((skill) => skill !== skillToRemove);
    setSelectedSkills(newSkillArray);
    handleChange({ target: { value: newSkillArray, name: "skills_required" } });
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").trim();
  };

  const handleSelectMethod = (method) => {
    setJdMethod(method);
    if (method === "ai") {
      handleWriteWithAI();
    }
  };

  const handleResetMethod = () => {
    const activeContent = isOnDuplicatePost
      ? duplicateData.about_company
      : newJob.about_company;
    const hasContent = stripHtmlTags(activeContent || "");

    if (hasContent) {
      setShowOverwriteConfirm(true);
    } else {
      setJdMethod(null);
    }
  };

  const handleWriteWithAI = async () => {
    setAiLoading(true);
    try {
      const currentData = isOnDuplicatePost ? duplicateData : newJob;
      const jobData = {
        company_name: currentData.campany_name || "",
        location: currentData.location || "",
        role_title: currentData.role_title || "",
        reports_to: currentData.reports_to || "",
        job_level: currentData.job_level || "",
        job_type: currentData.job_type || "",
        industry: currentData.industry || "",
        country: state.country || selectedCountry || "",
      };

      if (!jobData.company_name.trim()) {
        cogoToast.warn("Please fill in the company name first");
        setAiLoading(false);
        setJdMethod(null);
        return;
      }
      if (!jobData.role_title.trim()) {
        cogoToast.warn("Please fill in the role title first");
        setAiLoading(false);
        setJdMethod(null);
        return;
      }

      const generatedDescription =
        await aiJobDescriptionService.generateJobDescription(jobData);
      setNewJob({ ...newJob, about_company: generatedDescription });
      cogoToast.success("Job description generated successfully!");
    } catch (error) {
      const errorMessage =
        error.message ||
        "Failed to generate job description. Please try again.";
      cogoToast.error(errorMessage);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRewriteWithAI = async () => {
    const currentData = isOnDuplicatePost ? duplicateData : newJob;
    const currentContent = currentData.about_company || "";
    const cleanedContent = stripHtmlTags(currentContent);

    if (!cleanedContent) {
      cogoToast.warn("Please add some content to rewrite first");
      return;
    }
    if (cleanedContent.length < 20) {
      cogoToast.warn("Please add more content before using rewrite feature");
      return;
    }

    setAiLoading(true);
    try {
      const jobData = {
        role_title: currentData.role_title || "",
        company_name: currentData.campany_name || "",
      };
      const rewrittenDescription =
        await aiJobDescriptionService.rewriteJobDescription(
          currentContent,
          jobData,
        );
      setNewJob({ ...newJob, about_company: rewrittenDescription });
      cogoToast.success("Job description rewritten successfully!");
    } catch (error) {
      const errorMessage =
        error.message || "Failed to rewrite job description. Please try again.";
      cogoToast.error(errorMessage);
    } finally {
      setAiLoading(false);
    }
  };

  const currentTier = isOnDuplicatePost
    ? duplicateData.job_tier
    : newJob.job_tier;

  const [showTierPricing, setShowTierPricing] = useState(false);

  const setTierAndClose = (tierValue) => {
    handleChange({ target: { value: String(tierValue), name: "job_tier" } });
    setShowTierPricing(false);
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <div className="row mx-0">
        <div className="col-12 col-md-6">
          <CustomInput
            type="text"
            onChange={handleChange}
            name="campany_name"
            value={
              isOnDuplicatePost
                ? duplicateData.campany_name
                : newJob.campany_name
            }
            placeholder=""
            label="Name Of Company"
            id="name"
          />
        </div>

        <div className="col-12 col-md-6">
          <FormControl
            variant="outlined"
            fullWidth
            className="w-100 mb-4 rounded"
          >
            <SearchLocationInput
              inputStyle="TextField"
              handleLocationChange={(e) =>
                handleChange({ target: { value: e, name: "location" } })
              }
            />
          </FormControl>
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "-5px" }}>
          <CustomSelect
            handleChange={handleChange}
            className="fullwidth-select"
            options={{
              0: "Select Staff Strength",
              "Sole Employee": "Sole Employee",
              "2-20": "2-20",
              "21-50": "21-50",
              "51-200": "51-200",
              "201-500": "201-500",
              "501-1000": "501-1000",
              "1,001-10,000": "1,001-10,000",
              "10,001-50,000": "10,001-50,000",
              "50,001+": "50,001+",
            }}
            id="no_of_staff"
            name="no_of_staff"
            label="Staff Strength"
            currentValue={
              isOnDuplicatePost ? duplicateData.no_of_staff : newJob.no_of_staff
            }
          />
        </div>
        <div className="col-12 col-md-6">
          <CustomInput
            type="text"
            onChange={handleChange}
            name="role_title"
            value={
              isOnDuplicatePost ? duplicateData.role_title : newJob.role_title
            }
            label="Role title"
            id="role"
          />
        </div>
        <div className="col-12 col-md-6">
          <CustomInput
            type="text"
            onChange={handleChange}
            name="reports_to"
            value={
              isOnDuplicatePost ? duplicateData.reports_to : newJob.reports_to
            }
            label="Reports To"
            id="report"
          />
        </div>
        <div className="col-12 col-md-6" style={{ marginTop: "-4px" }}>
          <CustomSelect
            handleChange={handleChange}
            options={{ 0: "Select Level", ...levelOptions }}
            id="level"
            name="job_level"
            label="Job Levels"
            currentValue={
              isOnDuplicatePost ? duplicateData.job_level : newJob.job_level
            }
          />
        </div>

        <div className="col-12 col-md-6">
          <CustomSelect
            handleChange={handleChange}
            options={{
              0: "Select Type",
              "part-time": "Part-time",
              "full-time": "Full-time",
              contract: "Contract",
              internship: "Internship",
              volunteer: "Volunteer",
            }}
            id="job_type"
            name="job_type"
            label="Type of Employment"
            currentValue={
              isOnDuplicatePost ? duplicateData.job_type : newJob.job_type
            }
          />
        </div>

        <div className="col-12 col-md-6">
          <CustomSelect
            handleChange={handleChange}
            options={{ 0: "Select an Industry", ...industryOptions }}
            id="industry"
            name="industry"
            label="Industry"
            currentValue={
              isOnDuplicatePost ? duplicateData.industry : newJob.industry
            }
          />
        </div>
        <div className="col-12 col-md-6">
          <CustomSelect
            handleChange={handleChange}
            options={{
              0: "Select an Job Option",
              1: "Hybrid",
              2: "Remote",
              3: "On Site",
            }}
            id="worktype"
            name="worktype"
            label="Work Type"
            currentValue={newJob.worktype}
          />
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "18px" }}>
          <InputLabel name="country-label mt-3">Select Country</InputLabel>
          <Select
            labelId="country"
            name="country"
            label="country"
            value={state.country}
            onChange={handleChangeCountry}
            className="fullwidth-select"
          >
            <MenuItem value="">Select a Country</MenuItem>
            {countries.map(({ name, id }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "-4px" }}>
          <CustomSelect
            handleChange={handleChange}
            options={(() => {
              const baseOptions = {
                0: "Select Job Tier",
                1: "Tier 1",
                2: "Tier 2",
              };

              // Only include Tier 3 if user has canPostTier3 permission
              if (userData && userData.canPostTier3 === "1") {
                baseOptions[3] = "Tier 3";
              }

              return baseOptions;
            })()}
            id="job_tier"
            name="job_tier"
            label="Job Tier"
            currentValue={currentTier}
          />
          <div style={{ marginTop: "6px" }}>
            <span
              onClick={() => setShowTierPricing(true)}
              style={{
                fontSize: "10px",
                color: "#3a6ed4",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Not sure which tier? View pricing & features
            </span>
          </div>
        </div>

        {currentTier === "2" && (
          <div className="col-12 col-md-6">
            <CustomInput
              type="number"
              onChange={handleChange}
              name="estimated_views"
              value={
                isOnDuplicatePost
                  ? duplicateData.estimated_views || ""
                  : newJob.estimated_views || ""
              }
              placeholder="Enter estimated views"
              label="Estimated Views"
              id="estimated_views"
              min="1"
            />
            <EstimatedViewsCalculation
              views={
                isOnDuplicatePost
                  ? duplicateData.estimated_views
                  : newJob.estimated_views
              }
              selectedCountry={state.country}
            />
          </div>
        )}
        
        {/* About Hiring Manager Section */}
        <div className="col-12 col-md-12">
          <br />
          <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
            <label style={{ fontWeight: "500", margin: 0 }}>
              About Hiring Manager
            </label>
            <Tooltip
              title="A short note about the hiring manager gives candidates clarity on who they'll work with, builds trust, and attracts better aligned applicants. It turns your job post from a list of tasks into a real opportunity."
              placement="top"
              arrow
            >
              <HelpOutlineIcon
                style={{
                  fontSize: "18px",
                  marginLeft: "6px",
                  color: "#6c757d",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </div>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            name="about_hiring_manager"
            value={
              isOnDuplicatePost
                ? duplicateData.about_hiring_manager || ""
                : newJob.about_hiring_manager || ""
            }
            onChange={handleChange}
            placeholder="A short note about the hiring manager builds trust and attracts better aligned candidates by showing who leads the role."
            helperText={`${(isOnDuplicatePost ? duplicateData.about_hiring_manager || "" : newJob.about_hiring_manager || "").length}/700 characters (optional)`}
            inputProps={{ maxLength: 700 }}
            style={{ marginBottom: "16px" }}
          />
        </div>

        <div className="col-12 col-md-12">
          <br />
          <label
            style={{ fontWeight: "500", marginBottom: "8px", display: "block" }}
          >
            About Company/Role
          </label>

          {/* Method Selection — shown when no method is chosen and no existing content */}
          {!jdMethod &&
            !stripHtmlTags(
              (isOnDuplicatePost
                ? duplicateData.about_company
                : newJob.about_company) || "",
            ) && (
              <JDMethodSelector
                selectedMethod={jdMethod}
                onSelect={handleSelectMethod}
              />
            )}

          {/* Change method link — shown when a method is chosen or content exists */}
          {(jdMethod ||
            !!stripHtmlTags(
              (isOnDuplicatePost
                ? duplicateData.about_company
                : newJob.about_company) || "",
            )) && (
            <div style={{ marginBottom: "8px", textAlign: "right" }}>
              <span
                onClick={handleResetMethod}
                style={{
                  fontSize: "13px",
                  color: "#3a6ed4",
                  cursor: "pointer",
                  textDecoration: "none",
                  fontWeight: "500",
                }}
              >
                ↩ Change method
              </span>
            </div>
          )}

          {/* File Uploader — shown when upload method is selected and editor not yet populated */}
          {jdMethod === "upload" && !stripHtmlTags(newJob.about_company) && (
            <JDFileUploader
              onParsed={(htmlContent) => {
                setNewJob({ ...newJob, about_company: htmlContent });
              }}
              onCancel={() => setJdMethod(null)}
            />
          )}

          {/* AI Loading Indicator */}
          {jdMethod === "ai" && aiLoading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 24px",
                border: "2px solid #e0e0e0",
                borderRadius: "12px",
                backgroundColor: "#fafbfc",
              }}
            >
              <div style={{ marginBottom: "16px" }}>
                <CircularProgress size={40} style={{ color: "#3a6ed4" }} />
              </div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "500",
                  color: "#1a1a1a",
                }}
              >
                Generating job description with AI...
              </div>
              <div
                style={{ fontSize: "13px", color: "#6c757d", marginTop: "6px" }}
              >
                This may take a moment
              </div>
            </div>
          )}

          {/* Editor — shown when method is selected (manual/ai-done/upload-done) or content exists */}
          {(jdMethod === "manual" ||
            (jdMethod === "ai" && !aiLoading) ||
            (jdMethod === "upload" &&
              !!stripHtmlTags(newJob.about_company || "")) ||
            !!stripHtmlTags(
              (isOnDuplicatePost
                ? duplicateData.about_company
                : newJob.about_company) || "",
            )) && (
            <div style={{ position: "relative" }}>
              <style>
                {`
          .custom-quill-editor {
            background: white;
            border-radius: 8px;
            position: relative;
          }
          .custom-quill-editor .ql-toolbar {
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
            background-color: #f8f9fa;
            border: 1px solid #d1d5db;
            border-bottom: none;
            padding: 12px;
          }
          .custom-quill-editor .ql-container {
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
            border: 1px solid #d1d5db;
            font-family: inherit;
            position: relative;
          }
          .custom-quill-editor .ql-editor {
            min-height: 320px;
            font-size: 15px;
            line-height: 1.6;
            color: #000000;
            text-align: left !important;
          }
          .custom-quill-editor .ql-editor h1,
          .custom-quill-editor .ql-editor h2,
          .custom-quill-editor .ql-editor h3,
          .custom-quill-editor .ql-editor p,
          .custom-quill-editor .ql-editor ul,
          .custom-quill-editor .ql-editor ol {
            text-align: left !important;
          }
          .custom-quill-editor .ql-editor ul li::before,
          .custom-quill-editor .ql-editor ol li::before {
            color: #000000 !important;
          }
          .custom-quill-editor .ql-editor ul li,
          .custom-quill-editor .ql-editor ol li {
            color: #000000 !important;
          }
          .custom-quill-editor .ql-editor p {
            color: #000000;
            margin-bottom: 0.8em;
          }
          .custom-quill-editor .ql-editor.ql-blank::before {
            color: #9ca3af;
            font-style: italic;
          }
          .custom-quill-editor .ql-editor h1 {
            font-size: 2em;
            font-weight: bold;
            color: #000000;
            margin: 0.67em 0;
          }
          .custom-quill-editor .ql-editor h2 {
            font-size: 1.5em;
            font-weight: bold;
            color: #000000;
            margin: 0.75em 0;
          }
          .custom-quill-editor .ql-editor h3 {
            font-size: 1.17em;
            font-weight: bold;
            color: #000000;
            margin: 0.83em 0;
          }
          .custom-quill-editor .ql-tooltip {
            position: absolute !important;
            left: 50% !important;
            top: 50% !important;
            transform: translate(-50%, -50%) !important;
            background-color: #fff !important;
            border: 1px solid #0d6efd !important;
            box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25) !important;
            padding: 16px 20px !important;
            border-radius: 8px !important;
            z-index: 1000 !important;
            min-width: 400px !important;
          }
          .custom-quill-editor .ql-tooltip input[type=text] {
            border: 1px solid #0d6efd !important;
            padding: 10px 14px !important;
            border-radius: 6px !important;
            font-size: 14px !important;
            width: 280px !important;
            margin-right: 8px !important;
            color: #000 !important;
          }
          .custom-quill-editor .ql-tooltip input[type=text]:focus {
            border-color: #0d6efd !important;
            outline: none !important;
            box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2) !important;
          }
          .custom-quill-editor .ql-tooltip a.ql-action,
          .custom-quill-editor .ql-tooltip a.ql-remove {
            padding: 8px 18px !important;
            background-color: #0d6efd !important;
            color: #fff !important;
            border-radius: 6px !important;
            text-decoration: none !important;
            display: inline-block !important;
            margin-left: 6px !important;
            font-weight: 500 !important;
            transition: background-color 0.2s !important;
          }
          .custom-quill-editor .ql-tooltip a.ql-action:hover {
            background-color: #0b5ed7 !important;
          }
          .custom-quill-editor .ql-tooltip a.ql-remove {
            background-color: #dc3545 !important;
          }
          .custom-quill-editor .ql-tooltip a.ql-remove:hover {
            background-color: #bb2d3b !important;
          }
          .custom-quill-editor .ql-tooltip a.ql-action::after {
            content: 'Save' !important;
            border: none !important;
            margin: 0 !important;
          }
          .custom-quill-editor .ql-tooltip a.ql-remove::before {
            content: 'Remove' !important;
            margin: 0 !important;
          }
          .custom-quill-editor .ql-tooltip::before {
            content: 'Enter link:' !important;
            margin-right: 8px !important;
            color: #374151 !important;
            font-weight: 600 !important;
            display: block !important;
            margin-bottom: 8px !important;
          }
          .custom-quill-editor .ql-snow.ql-toolbar button:hover,
          .custom-quill-editor .ql-snow .ql-toolbar button:hover,
          .custom-quill-editor .ql-snow.ql-toolbar button.ql-active,
          .custom-quill-editor .ql-snow .ql-toolbar button.ql-active,
          .custom-quill-editor .ql-snow.ql-toolbar .ql-picker-label:hover,
          .custom-quill-editor .ql-snow .ql-toolbar .ql-picker-label:hover,
          .custom-quill-editor .ql-snow.ql-toolbar .ql-picker-label.ql-active,
          .custom-quill-editor .ql-snow .ql-toolbar .ql-picker-label.ql-active {
            color: #0d6efd !important;
          }
          .custom-quill-editor .ql-snow.ql-toolbar button:hover .ql-stroke,
          .custom-quill-editor .ql-snow .ql-toolbar button:hover .ql-stroke,
          .custom-quill-editor .ql-snow.ql-toolbar button.ql-active .ql-stroke,
          .custom-quill-editor .ql-snow .ql-toolbar button.ql-active .ql-stroke {
            stroke: #0d6efd !important;
          }
          .custom-quill-editor .ql-snow.ql-toolbar button:hover .ql-fill,
          .custom-quill-editor .ql-snow .ql-toolbar button:hover .ql-fill,
          .custom-quill-editor .ql-snow.ql-toolbar button.ql-active .ql-fill,
          .custom-quill-editor .ql-snow .ql-toolbar button.ql-active .ql-fill {
            fill: #0d6efd !important;
          }
          .custom-quill-editor .ql-editor blockquote {
            border-left: 4px solid #0d6efd;
            padding-left: 16px;
            margin-left: 0;
            font-style: italic;
            color: #555555;
          }
          .custom-quill-editor .ql-editor a {
            color: #0d6efd;
            text-decoration: underline;
          }
          .custom-quill-editor .ql-container.ql-snow:focus-within {
            border-color: #0d6efd;
            box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
          }
          .custom-quill-editor .ql-editor iframe {
            max-width: 100%;
            border-radius: 8px;
            margin: 1em 0;
          }
          .custom-quill-editor .ql-video {
            width: 100%;
            height: 400px;
          }
          .custom-quill-editor .ql-tooltip[data-mode=link]::before {
            display: none !important;
          }
          .custom-quill-editor .ql-tooltip.ql-editing {
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
        `}
              </style>
              <ReactQuill
                className="custom-quill-editor"
                theme="snow"
                value={
                  isOnDuplicatePost
                    ? duplicateData.about_company || ""
                    : newJob.about_company || ""
                }
                onChange={(content) => {
                  setNewJob({ ...newJob, about_company: content });
                }}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write about the company and role..."
              />
              <AIJobDescriptionIcons
                onWriteWithAI={handleWriteWithAI}
                onRewriteWithAI={handleRewriteWithAI}
                isLoading={aiLoading}
                hasExistingContent={
                  !!stripHtmlTags(
                    isOnDuplicatePost
                      ? duplicateData.about_company
                      : newJob.about_company,
                  )
                }
              />
            </div>
          )}

          {/* Overwrite Confirmation Dialog */}
          <Dialog
            open={showOverwriteConfirm}
            onClose={() => setShowOverwriteConfirm(false)}
            PaperProps={{
              style: {
                borderRadius: "12px",
                padding: "8px",
                maxWidth: "420px",
              },
            }}
          >
            <DialogTitle>Change Method?</DialogTitle>
            <DialogContent>
              <p style={{ color: "#6c757d", margin: 0 }}>
                This will clear your current job description content. Are you
                sure you want to continue?
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setShowOverwriteConfirm(false)}
                style={{ color: "#6c757d" }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setNewJob({ ...newJob, about_company: "" });
                  setJdMethod(null);
                  setShowOverwriteConfirm(false);
                }}
                style={{
                  backgroundColor: "#3a6ed4",
                  color: "#fff",
                  borderRadius: "6px",
                  padding: "6px 20px",
                }}
              >
                Clear & Change
              </Button>
            </DialogActions>
          </Dialog>
        </div>

        <div className="col-12 col-md-12">
          <SalaryRow
            handleChange={handleChange}
            salary_range={
              isOnDuplicatePost
                ? duplicateData.salary_range
                : newJob.salary_range
            }
            duration={
              isOnDuplicatePost ? duplicateData.duration : newJob.duration
            }
            currency={
              isOnDuplicatePost ? duplicateData.currency : newJob.currency
            }
          />
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "18px" }}>
          <FormControl variant="outlined" fullWidth className="w-100">
            <Autocomplete
              multiple
              freeSolo
              disableClearable
              id="skills"
              options={(skillOptions || []).map(({ title }) => title)}
              value={selectedSkills}
              onChange={handleSkillsChange}
              filterSelectedOptions
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              filterOptions={(options, params) => {
                const filter = createFilterOptions();
                return filter(options, params);
              }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.title || ""
              }
              renderOption={(option) =>
                typeof option === "string" ? option : option?.title || ""
              }
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Skills"
                  placeholder="Add Skill (ex: Interior Design)"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                />
              )}
            />
          </FormControl>
          <div className="skills-count mt-3 d-flex align-items-center">
            <span className="blob rounded px-2 py-1 bg-green co-white">
              <b>{selectedSkills.length}</b>
            </span>
            <span className="ml-2 co-green medium-text">Skills count</span>
          </div>
          {selectedSkills.length > 0 && (
            <div className="mt-5 w-100 d-flex flex-wrap">{displaySkills()}</div>
          )}
        </div>

        <div className="col-12" style={{ marginTop: "5.5%" }}>
          <RadioGroup
            name="isCompanyRepresentative"
            value={
              isOnDuplicatePost
                ? duplicateData.isCompanyRepresentative
                : newJob.isCompanyRepresentative
            }
            onChange={handleChange}
          >
            <label>Are you a company representative?</label>
            <div className="d-flex">
              <FormControlLabel
                value="1"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="0"
                control={<Radio color="primary" />}
                label="No"
              />
            </div>
          </RadioGroup>
        </div>
        {/* <div className="col-12">
          <RadioGroup
            name="canRecruiterRequest"
            value={
              isOnDuplicatePost
                ? duplicateData.canRecruiterRequest
                : newJob.canRecruiterRequest
            }
            onChange={handleChange}
          >
            <label>Allow recruiter reach out to you</label>
            <div className="d-flex">
              <FormControlLabel
                value="1"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="0"
                control={<Radio color="primary" />}
                label="No"
              />
            </div>
          </RadioGroup>
        </div> */}

        {/* <div className="col-12">
          <label>
            <strong>Post Job to Third-Party?</strong>
          </label>
          <br />
          <input
            type="checkbox"
            checked={externalPlatforms.includes("linkedin")}
            onChange={() => handleExternalPlatformChange("linkedin")}
          />{" "}
          Linkedin <br />
        </div> */}
      </div>
      <Dialog
          open={showTierPricing}
          onClose={() => setShowTierPricing(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={fullScreen}
          PaperProps={{
            style: { borderRadius: "12px", padding: "6px" },
          }}
        >
          <style>
            {`
              .pricing-close-btn:hover {
                background-color: #f3f4f6;
                border-radius: 50%;
              }
            `}
          </style>
          <DialogTitle
            style={{
              paddingRight: "56px", 
              fontWeight: 600,
            }}
          >
            Job Tiers - Features & Pricing

            <IconButton
              aria-label="close"
              onClick={() => setShowTierPricing(false)}
              style={{
                position: "absolute",
                right: 12,
                top: 12,
                transition: "background-color 0.2s ease",
              }}
              classes={{ root: "pricing-close-btn" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
                <thead>
                  <tr>
                    <th style={tableStyles.thLeft}>Feature</th>
                    <th style={tableStyles.th}>
                      Direct-to-Talent Jobs
                      <br />
                      <span style={tableStyles.subhead}>(Tier 1)</span>
                    </th>
                    <th style={tableStyles.th}>
                      Community Boosted Jobs
                      <br />
                      <span style={tableStyles.subhead}>(Tier 2)</span>
                    </th>
                    <th style={tableStyles.th}>
                      Full Placement Jobs
                      <br />
                      <span style={tableStyles.subhead}>(Tier 3)</span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {[
                    ["Pricing Model", "Free", "Per Unique Referred Viewer", "Placement Fee (% of Annual Pay)"],
                    ["Job Visibility", "Standard feed", "Boosted feed through community referrals", "Boosted feed through community referrals with unlimited referral views"],
                    ["Applications", "Organic only", "Pay-per-unique referred viewer", "Unlimited referred views"],
                    ["Cost per Job View", "—", "₦1,500 ($1.50) / view", "—"],
                    ["Default Spend Cap", "—", "100 Views", "—"],
                    ["Referral Rewards", "—", "—", "Included ✅"],
                    ["Referral Payout", "—", "—", "3% of annual salary"],
                    ["Match Score", "Not Included", "Included ✅", "Included ✅"],
                    ["Talent Ranking (AI)", "Not Included", "Included ✅", "Included"],
                    ["Ideal Talent Nudge (AI)", "Not Included", "Not Included", "Included ✅"],
                    ["Placement Fee", "—", "—", "10% of annual salary"],
                    ["Upfront Payment", "—", "Wallet funded", "30% of placement fee"],
                    ["Best For", "Small teams, trials, start-ups", "Fast visibility & reach and employer branding initiative with hiring", "Mid-Level / Senior / critical hires and employer branding initiative with hiring"],
                  ].map(([feature, t1, t2, t3]) => (
                    <tr key={feature}>
                      <td style={tableStyles.tdLeft}>{feature}</td>
                      <td style={tableStyles.td}>{t1}</td>
                      <td style={tableStyles.td}>{t2}</td>
                      <td style={tableStyles.td}>{t3}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={tableStyles.tdLeft}></td>

                    <td style={{ ...tableStyles.tfootTd, textAlign: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={() => setTierAndClose(1)}
                        style={{ borderRadius: "8px" }}
                      >
                        Select Tier 1
                      </Button>
                    </td>

                    <td style={{ ...tableStyles.td, textAlign: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={() => setTierAndClose(2)}
                        style={{ borderRadius: "8px" }}
                      >
                        Select Tier 2
                      </Button>
                    </td>

                    <td style={{ ...tableStyles.td, textAlign: "center" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setTierAndClose(3)}
                        style={{ borderRadius: "8px" }}
                      >
                        Select Tier 3
                      </Button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div style={{ marginTop: "10px", color: "#6c757d", fontSize: "12.5px" }}>
              Tier 3 requires an upfront payment (30% of placement fee) before posting.
            </div>
          </DialogContent>
        </Dialog>
    </>
  );
};

JobDetails.propTypes = {
  newJob: object.isRequired,
  handleChange: func.isRequired,
  updateSelectedCountryID: func.isRequired,
  setNewJob: func.isRequired,
  industryOptions: object.isRequired,
  levelOptions: object.isRequired,
  skillOptions: object.isRequired,
  duplicateData: object,
  isOnDuplicatePost: bool,
};

JobDetails.defaultProps = {
  duplicateData: {},
  isOnDuplicatePost: false,
};

const EstimatedViewsCalculation = ({ views, selectedCountry }) => {
  const viewCount = parseInt(views) || 0;
  if (viewCount === 0) return null;

  const isNigeria = selectedCountry === "160";
  const ratePerView = isNigeria ? 1500 : 1.50;
  const currency = isNigeria ? "NGN" : "USD";
  const currencySymbol = isNigeria ? "₦" : "$";
  const totalCost = viewCount * ratePerView;
  const formattedCost = totalCost.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="mt-2 p-2 bg-light rounded">
      <small className="text-muted">
        <strong>Cost Calculation:</strong>
        <br />
        {viewCount.toLocaleString()} views × {currencySymbol}
        {ratePerView} per view = {currencySymbol}
        {formattedCost} {currency}
      </small>
    </div>
  );
};

const SalaryRow = ({ handleChange, duration, salary_range, currency }) => {
  // salary_range is optional for all tiers
  const inputName = "salary_range";
  const inputLabel = "Salary Range (Optional)";

  const [displayValue, setDisplayValue] = useState(salary_range || "");

  useEffect(() => {
    setDisplayValue(
      salary_range !== undefined && salary_range !== null
        ? String(salary_range)
        : "",
    );
  }, [salary_range]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setDisplayValue(value);
    handleChange({ target: { value, name: inputName } });
  };

  const handleBlur = (e) => {
    const text = e.target.value || "";
    if (/^\d+$/.test(text.replace(/,/g, ""))) {
      const stripped = text.replace(/,/g, "");
      const formatted = stripped
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      setDisplayValue(formatted);
      handleChange({ target: { value: formatted, name: inputName } });
    }
  };

  const handleCurrencyChange = (e) => {
    handleChange({ target: { value: e.target.value, name: "currency" } });
  };

  return (
    <div
      className="col-12"
      style={{
        margin: 0,
        padding: 0,
      }}
    >
      <div
        className="col"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          margin: 0,
          padding: 0,
        }}
      >
        <div className="col-12 col-md-4 p-0">
          <CustomInput
            type="text"
            name={inputName}
            label={inputLabel}
            id="report-salary"
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="e.g. 50000 or 40000 - 60000"
          />
        </div>
        <div className="col-12 col-md-4">
          <CustomSelect
            handleChange={handleCurrencyChange}
            options={{
              0: "Select Currency",
              ...CURRENCY_OPTIONS.reduce(
                (acc, option) => ({ ...acc, [option.value]: option.label }),
                {},
              ),
            }}
            id="currency"
            name="currency"
            label="Currency"
            currentValue={currency || "0"}
          />
        </div>
        <div className="col-12 col-md-4">
          <CustomSelect
            handleChange={handleChange}
            options={{
              0: "Select Duration",
              1: "Hourly",
              2: "Daily",
              3: "Weekly",
              4: "Monthly",
              5: "Annually",
            }}
            id="duration"
            name="duration"
            label="Duration"
            currentValue={duration}
          />
        </div>
      </div>
    </div>
  );
};

SalaryRow.propTypes = {
  handleChange: func.isRequired,
  duration: string,
  salary_range: string,
  currency: string,
};

const tableStyles = {
  thLeft: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e6e6e6",
    background: "#fafbfc",
    position: "sticky",
    left: 0,
    zIndex: 1,
    minWidth: 180,
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e6e6e6",
    background: "#fafbfc",
    minWidth: 210,
    verticalAlign: "bottom",
  },
  subhead: {
    fontSize: "12px",
    color: "#6c757d",
    fontWeight: 500,
  },
  tdLeft: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fff",
    position: "sticky",
    left: 0,
    zIndex: 1,
    minWidth: 180,
    fontWeight: 600,
  },
  td: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fff",
    verticalAlign: "top",
  },
  tfootTd: {
  padding: "14px 12px",
  borderTop: "1px solid #e6e6e6",
  background: "#fff",
  }
};

export default JobDetails;
